import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import { GetSingleSession } from "../../../../../connection/sessions";
import {
	loadWorkingSession,
	setCurrentLanguage,
	setSessionLoading
} from "../../../../../store/actions/admin/create-event/session";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";

import { FeatureFlagsEnum, Session } from "../../../../../types/working-model";
import useRerenderQuillOnLanguageChange from "../../../../../utils/use-rerender-quill-on-language-change";
import EventTranslationsModal from "../../../header/event-translations-modal";
import { switchingLanguageAlert } from "../../homepage/editor/header/header";
import useTranslationsV2 from "../../shared/use-translations-v2";

interface ISessionEditorHeaderProps {
	session: Session;
}

const MAX_DEFAULT_LANGUAGES = 11;

const SessionEditorHeader: React.FC<ISessionEditorHeaderProps> = ({ session }) => {
	const {
		AuthReducer: {
			token
		}
	} = useTypedSelector((state) => state);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const languagesContainerRef = useRef<HTMLDivElement | null>(null);
	const useV2 = useTranslationsV2();
	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams<any>();

	const [maxDisplayedLanguages, setMaxDisplayedLanguages] = useState(MAX_DEFAULT_LANGUAGES);
	const [showExpandedBtn, setShowExpandedBtn] = useState(false);
	const [showEventTranslationsModal, setShowEventTranslationsModal] = useState(false);

	const languages = session.languages;

	useRerenderQuillOnLanguageChange();


	const handleLanguageClick = (language: string) => async () => {
		if (params.id && params.session && token) {
			if (!useV2) {
				dispatch(setSessionLoading(true));
				switchingLanguageAlert();

				const _session = await GetSingleSession(token, session.session);

				if (_session) {
					dispatch(loadWorkingSession(_session));
				}
			}

			history.replace(`/admin/events/create/${params.id}/session/${params.session}/${language}`);
			dispatch(setCurrentLanguage(language));
			dispatch(setSessionLoading(false));
		}
	};

	const handleExpandLanguage = () => {
		setMaxDisplayedLanguages(languages.length);
		setShowExpandedBtn(true);
	};

	const handleMinimizeExpandLanguage = () => {
		setMaxDisplayedLanguages(MAX_DEFAULT_LANGUAGES);
		setShowExpandedBtn(false);
	};

	const sortedLanguages = useMemo(() => {
		return [session.default_language, ...languages.filter((lang: string) => lang !== session.default_language).sort()];
	}, [languages, session.default_language]);

	const allLanguages = useMemo(() => {
		if (!workingEvent) {
			return [];
		} else {
			const set = new Set(workingEvent.sessions.flatMap(session => session.languages));
			return Array.from(set).sort();
		}
	}, [workingEvent]);

	return (
		<div className="session-editor-header">
			{languages?.length >= 1 ? (
				<div className="session-editor-header-column session-editor-header-languages">
					<h6 className="session-editor-header-titles">Languages</h6>
					<div ref={languagesContainerRef} className="session-editor-language-container">
						{featureFlags[FeatureFlagsEnum.translations_v2] && (
							<div className="session-editor-language-item" style={{ marginRight: 8 }}>
								<button
									className={workingEvent?.settings.translation_version === 2 ? 'on' : ''}
									onClick={() => setShowEventTranslationsModal(true)}
								>
									Translations
								</button>
							</div>
						)}

						{sortedLanguages.map((language: string, index: number) => {
							if (index < maxDisplayedLanguages) {
								return (
									<div key={language} className="session-editor-language-item">
										<button
											className={params?.language === language ? 'active' : ''}
											onClick={handleLanguageClick(language)}
										>
											{language}
										</button>
									</div>
								);
							}
						})}

						{languages.length > maxDisplayedLanguages ? (
							< div className="session-editor-language-item">
								<button onClick={handleExpandLanguage}>
									+{languages.length - maxDisplayedLanguages}
								</button>
							</div>
						) :
							(showExpandedBtn) && (
								<div className="event-editor-language-item">
									<button onClick={handleMinimizeExpandLanguage}>
										-
									</button>
								</div>
							)}
					</div>
				</div>
			) : null}
			{featureFlags[FeatureFlagsEnum.translations_v2] && (
				<EventTranslationsModal
					open={showEventTranslationsModal}
					closeFn={() => setShowEventTranslationsModal(false)}
					languages={allLanguages}
				/>
			)}
		</div>
	);
};

export default SessionEditorHeader;
